<template>
  <div v-if="visible" class="alert-container">
    <div class="alert" :class="['alert-' + alertType, 'alert-' + context]">
      <div class="alert-icon">
        <NsIcon
          :icon-name="icon"
          :color="color"
          style="width: 30px; height: 30px"
        />
      </div>
      <div class="alert-head">
        <h4 class="title-small-bold">
          <slot name="head">
            {{ titleText }}
          </slot>
        </h4>
      </div>
      <div v-if="hasDismissListener" class="alert-close-button">
        <span>
          <svg
            viewBox="0 0 12 12"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            @click="$emit('dismiss')"
          >
            <g fill="none" stroke="#000000" stroke-width="1">
              <path stroke-linecap="round" d="M 0 0 L 12 12 M 0 12 L 12 0" />
            </g>
          </svg>
        </span>
      </div>
      <div class="alert-content">
        <div class="alert-body p-medium">
          <slot name="body" />
        </div>
        <div v-if="$slots.foot" class="alert-foot p-small">
          <slot name="foot" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Original Author: Aaron Turnblom
 * Original Team: Area 51
 */
import { NsIcon } from '@nuskin/ns-icon';

const ALERT_CONTEXT = ['error', 'info', 'notice', 'success', 'warning'];

const ALERT_TYPES = ['auto', 'banner', 'card'];

export default {
  name: 'NsAlert',
  components: {
    NsIcon,
  },
  props: {
    context: {
      type: String,
      default: 'info',
      validator(value) {
        return ALERT_CONTEXT.includes(value);
      },
    },
    title: {
      type: String,
      default: 'Information',
    },
    threshhold: {
      type: Number,
      default: 600,
    },
    type: {
      type: String,
      default: 'banner',
      validator(value) {
        return ALERT_TYPES.includes(value);
      },
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    alertType() {
      return this.type === 'auto'
        ? this.windowWidth < this.threshhold
          ? 'card'
          : 'banner'
        : this.type;
    },
    hasDismissListener() {
      return this.$listeners && this.$listeners.dismiss;
    },
    color() {
      switch (this.context) {
        case 'error':
          return '#C81D1D';
        case 'notice':
          return '#3783B9';
        case 'success':
          return '#5BAC85';
        case 'warning':
          return '#FD983B';
        default:
          return '#3783B9';
      }
    },
    icon() {
      switch (this.context) {
        case 'error':
          return 'icon-error-color-filled';
        case 'success':
          return 'icon-success-filled';
        case 'warning':
          return 'icon-warning-filled';
        default:
          return 'icon-info-filled';
      }
    },
    titleText() {
      if (this.title) {
        return this.title;
      } else {
        switch (this.context) {
          case 'error':
            return 'Error';
          case 'notice':
            return 'Notice';
          case 'success':
            return 'Success';
          case 'warning':
            return 'Warning';
          default:
            return 'Information';
        }
      }
    },
  },
  created() {
    window.addEventListener('resize', this.windowResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResize);
  },
  methods: {
    windowResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '~@nuskin/ns-core-styles/src/scss/colors';

$color-ash: map.get(colors.$theme, primary);
$color-blackhole: map.get(colors.$theme, primary);
$color-black: #000000;
$color-white: #ffffff;

$alert-context: (
  error: (
    color: map.get(colors.$theme, error),
  ),
  info: (
    color: map.get(colors.$theme, info),
  ),
  notice: (
    color: map.get(colors.$theme, info),
  ),
  success: (
    color: map.get(colors.$theme, success),
  ),
  warning: (
    color: map.get(colors.$theme, warning),
  ),
);

$alert-types: (
  banner: (
    padding-horizontal: 45px,
    padding-vertical: 15px,
    bar-position: 'top',
    content-column-start: row-center,
    content-column-end: row-center,
    foot-visibility: block,
  ),
  card: (
    padding-horizontal: 24px,
    padding-vertical: 24px,
    bar-position: 'left',
    content-column-start: row-left,
    content-column-end: row-right,
    foot-display: none,
  ),
);

* {
  box-sizing: border-box;
  webkit-font-smoothing: antialiased;
}

.alert-container {
  $gutter-width: 10px;

  $close-button-width: 12px;
  $close-button-height: 12px;
  $icon-width: 24px;
  $icon-height: 24px;

  .alert {
    position: relative;
    display: grid;
    grid-template-columns: [row-left] $icon-width [space] $gutter-width [row-center] auto [space] $gutter-width [row-right] $close-button-width;
    grid-template-rows: [row1] auto;
    grid-auto-row: auto;
    grid-auto-flow: row;
    background-color: $color-white;
    border: 1px solid $color-ash;
  }

  .alert-close-button {
    grid-column-start: row-right;
    grid-column-end: row-right;
    grid-row-start: row1;
    grid-row-end: row1;
    align-self: center;
  }

  .alert-icon {
    grid-column-start: row-left;
    grid-column-end: row-left;
    grid-row-start: row1;
    grid-row-end: row1;
    width: $icon-width;
    height: $icon-height;
  }

  .alert-head {
    grid-column-start: row-center;
    grid-column-end: row-center;
    grid-row-start: row1;
    grid-row-end: row1;
  }

  // .alert-banner, .alert-card, ...
  @each $type-name, $type in $alert-types {
    $padding-horizontal: map.get($type, 'padding-horizontal');
    $padding-vertical: map.get($type, 'padding-vertical');

    .alert-#{$type-name} {
      min-height: $icon-height + $padding-horizontal * 2;
      padding: $padding-vertical $padding-horizontal;

      // .alert-error, .alert-info, ...

      .alert-content {
        grid-column-start: map.get($type, 'content-column-start');
        grid-column-end: map.get($type, 'content-column-end');
      }

      .alert-foot {
        display: map.get($type, 'foot-display');
      }
    }
  }

  .alert-head {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .alert-content {
    margin-top: 8px;
    color: $color-blackhole;
  }

  .alert-foot {
    margin-top: 8px;
    color: $color-blackhole;
  }
}
</style>
